"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import buyPlan from "@/_assets/images/esim-buy-plan.png";
import installQr from "@/_assets/images/esim-install-qr.png";
import activatePlan from "@/_assets/images/esim-activate-plan.png";
import buyPlanTablet from "@/_assets/images/esim-buy-plan-tablet.png";
import installQrTablet from "@/_assets/images/esim-install-qr-tablet.png";
import activatePlanTablet from "@/_assets/images/esim-active-plan-tablet.png";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

// import { useState, useRef } from "react";
// import Image from "next/image";
// import { Card } from "@/components/ui/card";
// import {
//   Carousel,
//   CarouselContent,
//   CarouselItem,
// } from "@/components/ui/carousel";
// import { ChevronLeft, ChevronRight } from "lucide-react";
// import stepOne from "@/_assets/images/stepOne.png";
// import stepTwo from "@/_assets/images/stepTwo.png";
// import stepThree from "@/_assets/images/stepThree.png";
// import type { CarouselApi } from "@/components/ui/carousel";

interface EsimInstallationProps {
  countryName?: string;
}

export default function EsimSteps({ countryName }: EsimInstallationProps) {
  const t = useTranslations("Shared.esimSteps");
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [activeStep, setActiveStep] = useState(0);
  const linkClassName =
    "font-500 text-primary underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary";
  const countrySteps = (
    [
      {
        id: "step1",
        titleKey: countryName
          ? "steps.step1.dynamicTitle"
          : "steps.step1.title",
        descriptionKey: countryName
          ? "steps.step1.dynamicDescription"
          : "steps.step1.description",
        image: buyPlan,
        tabletImage: buyPlanTablet,
        href: "https://esimcard.com/blog/how-to/how-to-buy-esim-online/",
      },
      {
        id: "step2",
        titleKey: "steps.step2.title",
        descriptionKey: "steps.step2.description",
        image: installQr,
        tabletImage: installQrTablet,
        href: "https://esimcard.com/blog/how-to-get-esim-qr-code/",
      },
      {
        id: "step3",
        titleKey: "steps.step3.title",
        descriptionKey: countryName
          ? "steps.step3.dynamicDescription"
          : "steps.step3.description",
        image: activatePlan,
        tabletImage: activatePlanTablet,
        href: "https://esimcard.com/blog/travel-tips/how-to-use-esim-when-traveling/",
      },
    ] as const
  ).map((step) => ({
    ...step,
    heading: t(step.titleKey, { countryName: countryName ?? "" }),
    description: t.rich(step.descriptionKey, {
      countryName: countryName ?? "",
      link: (chunks) => (
        <a className={linkClassName} href={step.href}>
          {chunks}
        </a>
      ),
    }),
  }));

  useEffect(() => {
    if (!carouselApi) return;

    const updateActiveStep = () => {
      setActiveStep(carouselApi.selectedScrollSnap());
    };

    updateActiveStep();
    carouselApi.on("select", updateActiveStep);
    carouselApi.on("reInit", updateActiveStep);

    return () => {
      carouselApi.off("select", updateActiveStep);
      carouselApi.off("reInit", updateActiveStep);
    };
  }, [carouselApi]);

  // const [activeStep, setActiveStep] = useState(0);
  // const carouselApi = useRef<CarouselApi | null>(null);

  // const steps = stepsImages.map((image, idx) => ({
  //   id: idx + 1,
  //   image,
  //   heading: headings[idx] || defaultHeadings[idx],
  //   description: descriptions[idx] || defaultDescriptions[idx],
  // }));

  // const handleNextStep = () => {
  //   const newStep = (activeStep + 1) % steps.length;
  //   setActiveStep(newStep);
  //   carouselApi.current?.scrollTo(newStep);
  // };

  // const handlePrevStep = () => {
  //   const newStep = (activeStep - 1 + steps.length) % steps.length;
  //   setActiveStep(newStep);
  //   carouselApi.current?.scrollTo(newStep);
  // };

  // const handleStepClick = (index: number) => {
  //   setActiveStep(index);
  //   carouselApi.current?.scrollTo(index);
  // };

  return (
    <section className="mt-14 bg-background py-12 text-foreground sm:py-16">
      <div className="container">
        <div className="mx-auto max-w-[680px] text-center">
          <h2 className="font-montserrat text-[32px] font-600 leading-tight md:text-h2-md xl:text-h2-xl">
            {countryName ? t("dynamicTitle", { countryName }) : t("title")}
          </h2>
          <p className="mx-auto mt-3 text-lg leading-relaxed text-muted-foreground">
            {countryName
              ? t("dynamicDescription", { countryName })
              : t("description")}
          </p>
        </div>

        <Carousel
          opts={{ align: "start", loop: true }}
          setApi={setCarouselApi}
          className="mt-9 md:hidden"
        >
          <CarouselContent className="ms-0">
            {countrySteps.map((step, index) => (
              <CarouselItem key={step.id} className="ps-0">
                <article className="text-center">
                  <div className="mb-6 flex justify-center">
                    <span className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-primary text-base font-700 text-white">
                      {index + 1}
                    </span>
                  </div>

                  <Image
                    src={step.image}
                    alt=""
                    sizes="calc(100vw - 80px)"
                    className="mx-auto h-[238px] w-[278px] max-w-full rounded-[20px] object-cover"
                  />

                  <h3 className="mt-6 font-montserrat text-[23px] font-700 leading-snug">
                    {step.heading}
                  </h3>
                  <p className="mx-auto mt-3 max-w-[330px] text-base leading-[1.6] text-muted-foreground">
                    {step.description}
                  </p>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>

          <div className="mt-12 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => carouselApi?.scrollPrev()}
              aria-label="Previous step"
              className="flex h-6 w-6 items-center justify-center text-muted-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1.5" aria-label="Select step">
              {countrySteps.map((step, index) => (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => carouselApi?.scrollTo(index)}
                  aria-label={`Go to step ${index + 1}`}
                  aria-current={activeStep === index ? "step" : undefined}
                  className={`h-3 rounded-full transition-all ${
                    activeStep === index
                      ? "w-6 bg-primary"
                      : "w-3 bg-muted-foreground"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => carouselApi?.scrollNext()}
              aria-label="Next step"
              className="flex h-6 w-6 items-center justify-center text-muted-foreground"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </Carousel>

        <ol
          aria-label={
            countryName ? t("dynamicTitle", { countryName }) : t("title")
          }
          className="relative mt-11 hidden list-none p-0 before:absolute before:inset-x-[calc((100%-4rem)/6)] before:top-4 before:hidden before:border-t-2 before:border-dotted before:border-foreground/20 md:grid md:gap-8 xl:grid-cols-3 xl:before:block"
        >
          {countrySteps.map((step, index) => (
            <li
              key={step.id}
              className="relative w-[88%] shrink-0 snap-start md:w-auto md:min-w-0"
            >
              <div className="relative mb-4 flex h-8 items-center justify-center">
                <span
                  aria-hidden="true"
                  className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-700 text-white"
                >
                  {index + 1}
                </span>
              </div>
              <Image
                src={step.image}
                alt=""
                sizes="(min-width: 1280px) 33vw, 100vw"
                className="h-auto w-full rounded-2xl md:hidden xl:block"
              />
              <Image
                src={step.tabletImage}
                alt=""
                sizes="100vw"
                className="hidden h-auto w-full rounded-2xl md:block xl:hidden"
              />
              <h3 className="mt-4 text-2xl font-700 leading-snug">
                {step.heading}
              </h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                {step.description}
              </p>
            </li>
          ))}
        </ol>
      </div>
      {/* <div className="flex flex-col gap-[1.31rem]">
        <h2 className="text-center font-montserrat text-h2-base font-600 md:text-h2-md xl:text-start xl:text-h2-xl">
          {title ?? "How does eSIMCard eSIM work?"}
        </h2>
        <p className="text-center text-body-sm text-muted-foreground md:text-body-md xl:text-start">
          {description ?? (
            <>
              Set up your eSIM in minutes with three simple steps:
              <span className="inline sm:block">
                Purchase a data plan, scan the QR code to install, and activate
                instantly for global connectivity.
              </span>
            </>
          )}
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
        <div className="order-1 flex flex-col items-center justify-center md:order-1 lg:order-2">
          <div className="flex items-center rounded-[20px] px-[50px] pb-0 pt-10 md:px-[100px] lg:px-[120px]">
            <div className="relative h-auto w-full max-w-[260px] overflow-hidden sm:max-w-sm md:max-w-[351px]">
              <div
                className="transition-all duration-500 ease-out"
                style={{ transform: `translateX(${isRtl ? '' : '-'}${activeStep * 100}%)` }}
              >
                <div className="flex w-full">
                  {steps.map((step) => (
                    <div
                      key={step.id}
                      className="flex w-full flex-shrink-0 items-center justify-center"
                    >
                      <Image
                        src={step.image || "/placeholder.svg"}
                        alt={step.heading}
                        width={271}
                        height={550}
                        className="h-auto w-full object-contain"
                        priority={step.id === 1}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-center gap-2">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => handleStepClick(index)}
                className={`rounded-full transition-all duration-300 ${activeStep === index
                    ? "h-3 w-8 bg-primary"
                    : "h-3 w-3 bg-foreground/20 hover:bg-foreground/40"
                  }`}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="order-2 mx-auto hidden w-full max-w-[362px] space-y-6 md:max-w-[550px] lg:order-1 lg:-mt-16 lg:block lg:max-w-[650px]">
          {steps.map((step, index) => (
            <div
              key={step.id}
              role="button"
              tabIndex={0}
              onClick={() => handleStepClick(index)}
              onKeyDown={(e) => e.key === "Enter" && handleStepClick(index)}
            >
              <Card
                className={`p-6 shadow-none transition-all duration-300 md:p-8 ${
                  activeStep === index
                    ? "scale-105 border-l-2 border-l-primary bg-foreground-light/5 shadow-lg"
                    : "border-none bg-background hover:shadow-md"
                  }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-lg font-bold transition-all duration-300 md:h-14 md:w-14 md:text-xl ${activeStep === index
                        ? "bg-primary text-background shadow-lg dark:text-foreground"
                        : "border-2 border-grey-dark/10 bg-grey-light"
                      }`}
                  >
                    0{step.id}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h3
                      className={`mb-2 text-lg font-semibold transition-colors duration-300 md:text-xl ${activeStep === index
                          ? "text-primary"
                          : "text-foreground"
                        }`}
                    >
                      {step.heading}
                    </h3>
                    <p className="text-sm text-foreground-light md:text-base">
                      {step.description}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>

        <div className="order-2 w-full lg:order-1 lg:hidden">
          <Carousel
            opts={{ align: "start", loop: true, direction: isRtl ? "rtl" : "ltr" }}
            setApi={(api) => {
              if (!api) return;

              carouselApi.current = api;

              const onSelect = () => {
                setActiveStep(api.selectedScrollSnap());
              };

              onSelect();
              api.on("select", onSelect);

              return () => {
                api.off("select", onSelect);
              };
            }}
            className="w-full"
          >
            <CarouselContent className="-ms-2 md:-ms-4">
              {steps.map((step, index) => (
                <CarouselItem key={step.id} className="ps-2 md:ps-4">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleStepClick(index)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleStepClick(index)
                    }
                    className="mx-auto w-full max-w-[560px]"
                  >
                    <Card
                      className={`border-2 p-6 transition-all duration-300 md:p-8 ${activeStep === index
                          ? "scale-105 bg-foreground-light/5 shadow-lg"
                          : "border-grey-light bg-background hover:border-primary/40 hover:shadow-md"
                        }`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-lg font-bold transition-all duration-300 md:h-14 md:w-14 md:text-xl ${activeStep === index
                              ? "bg-primary text-background shadow-lg"
                              : "border-2 border-grey-dark/10 bg-grey-light"
                            }`}
                        >
                          0{step.id}
                        </div>

                        <div className="min-w-0 flex-1">
                          <h3
                            className={`mb-2 text-lg font-semibold transition-colors duration-300 md:text-xl ${activeStep === index
                                ? "text-primary"
                                : "text-foreground"
                              }`}
                          >
                            {step.heading}
                          </h3>
                          <p className="text-sm text-foreground-light md:text-base">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <div className="mt-6 flex items-center justify-center gap-6">
            <button
              onClick={handlePrevStep}
              aria-label="Previous Step"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-grey bg-primary shadow-sm transition hover:bg-primary focus:bg-primary focus:outline-none focus-visible:outline-none active:bg-primary"
            >
              <ChevronLeft className="h-6 w-6 text-background" />
            </button>
            <button
              onClick={handleNextStep}
              aria-label="Next Step"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-grey bg-primary shadow-sm transition hover:bg-primary focus:bg-primary focus:outline-none focus-visible:outline-none active:bg-primary"
            >
              <ChevronRight className="h-6 w-6 text-background" />
            </button>
          </div>
        </div>
      </div> */}
    </section>
  );
}
